import { clsx, type ClassValue } from 'clsx';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDateFormat(timeStamp: any) {
  const newDate = moment.unix(timeStamp).format('MMM DD');
  return newDate;
}

export function getTimeFormat(timeStamp: any) {
  const newTime = moment.unix(timeStamp).format('hh:mma');
  return newTime;
}

export const getTimeFormatFromUnix = (unix: number) => {
  const newTime = moment.unix(unix).format('hh:mma');
  return newTime;
};

type BusStopInfo = {
  tripStopMappingId?: number;
  stopIdx?: number;
  etaTimestamp?: number;
  etaMinute?: number;
  path?: string;
};

type BusLike = {
  version?: string;
  stopMapping?: { [key: number]: BusStopInfo };
  stopsList?: BusStopInfo[];
};

export function getBusStopInfo(
  busList: BusLike | undefined | null,
  targetId: number | undefined,
  stopIdx: string | undefined
): BusStopInfo | undefined {
  if (!busList) return undefined;
  if (
    busList.version === 'v2'
    && busList.stopMapping
    && Object.keys(busList.stopMapping).length > 0
  ) {
    return targetId !== undefined ? busList.stopMapping[targetId] : undefined;
  }
  return busList.stopsList?.find((sto) => sto?.stopIdx === Number(stopIdx));
}

// Shared stop-badge visual language (sheet timeline + map markers):
// first stop blue, last stop red, in-between gray — and green once passed.
// "Passed" reuses the VISITED rule: live bus data exists but carries no ETA
// for that stop.
export type StopVisualState = 'passed' | 'first' | 'last' | 'middle';

type StopLike = {
  target_id?: number;
  stop_index?: string;
  is_origin?: boolean;
  is_destination?: boolean;
};

export function getStopVisualStates(
  tripStops: StopLike[] | undefined | null,
  busList: BusLike | undefined | null
): StopVisualState[] {
  if (!tripStops?.length) return [];
  return tripStops.map((stop, index) => {
    if (busList) {
      const info = getBusStopInfo(busList, stop?.target_id, stop?.stop_index);
      if (!info?.etaMinute) return 'passed';
    }
    if (stop?.is_origin || index === 0) return 'first';
    if (stop?.is_destination || index === tripStops.length - 1) return 'last';
    return 'middle';
  });
}

export const stopBadgeClass = (state: StopVisualState): string => {
  switch (state) {
    case 'passed':
      return 'bg-ro-green text-white';
    case 'first':
      return 'bg-ro-blue text-white';
    case 'last':
      return 'bg-ro-red text-white';
    default:
      return 'border-2 border-ro-divider bg-white text-ro-muted';
  }
};

const SHEET_FLICK_THRESHOLD_PX = 40;

export function resolveSheetSnap(
  netDy: number,
  startHeight: number,
  maxHeight: number
): 'expanded' | 'collapsed' {
  if (Math.abs(netDy) > SHEET_FLICK_THRESHOLD_PX) {
    return netDy > 0 ? 'expanded' : 'collapsed';
  }
  const endHeight = Math.max(0, Math.min(maxHeight, startHeight + netDy));
  return endHeight > maxHeight / 2 ? 'expanded' : 'collapsed';
}

export const alphabet = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
];
